/**
 * Toggle Switch Component
 *
 * A styled toggle switch with teal active state
 * Controlled component - state is managed by parent
 */

interface ToggleProps {
  label?: string;
  /** Accessible name without visible text (use instead of `label` when the row already shows the text). */
  ariaLabel?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

export const Toggle = ({
  label,
  ariaLabel,
  checked = false,
  onChange,
  disabled = false
}: ToggleProps) => {
  if (import.meta.env.DEV && !label && !ariaLabel) {
    console.warn('[Toggle] No accessible name: pass `label` or `ariaLabel`.');
  }

  const handleToggle = () => {
    onChange?.(!checked);
  };

  return (
    <label className={`flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <div className="relative">
        <input
          type="checkbox"
          className="peer sr-only"
          role="switch"
          aria-checked={checked}
          checked={checked}
          disabled={disabled}
          onChange={handleToggle}
          aria-label={ariaLabel ?? label}
        />
        <div
          className={`
            w-10 h-6 rounded-full transition-colors duration-200 ease-in-out
            border-2 border-transparent peer-focus-visible:border-secondary-700
            ${checked ? 'bg-secondary-500' : 'bg-gray-300'}
          `}
        >
          <div
            className={`
              absolute top-1 left-1 w-4 h-4 rounded-full ${checked ? 'bg-secondary-contrast' : 'bg-white'}
              transition-transform duration-200 ease-in-out shadow-sm
              ${checked ? 'translate-x-4' : 'translate-x-0'}
            `}
          />
        </div>
      </div>
      {label && (
        <span className="ml-3 text-sm text-gray-700">{label}</span>
      )}
    </label>
  );
};
